import React, { useEffect, useRef, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { getProfilePictureUrl } from '../services/storageService';
import { MAPBOX_STYLE } from '../constants/mapbox';
import './Map.css';

// Mapbox access token
// IMPORTANT: For client-side use, you should use a PUBLIC token (starts with 'pk.')
// Secret tokens (starts with 'sk.') should only be used server-side
// Get your public token from https://account.mapbox.com/
const MAPBOX_TOKEN = import.meta.env.VITE_MAPBOX_TOKEN;

if (!MAPBOX_TOKEN) {
  console.error('Missing VITE_MAPBOX_TOKEN. Please check your .env.local file.');
  throw new Error('Mapbox token is required. Please set VITE_MAPBOX_TOKEN in your .env.local file.');
}

const Map = ({ 
  locations = [], 
  currentTime = null, 
  centerLatitude = 39.6061, 
  centerLongitude = -106.3550, 
  height = '100%',
  agentPositions = [],
  onAgentClick = null,
  highlightedSegment = null,
  positionSource = null
}) => {
  const mapContainer = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef([]);
  const agentMarkersRef = useRef([]);
  const [profilePictureUrls, setProfilePictureUrls] = useState(() => new window.Map()); // Map of profilePicturePath -> URL

  // Initialize map (only once, using initial center values)
  useEffect(() => {
    if (mapContainer.current && !mapRef.current) {
      // Set Mapbox access token
      mapboxgl.accessToken = MAPBOX_TOKEN;

      // Initialize Mapbox map with 3D outdoor style
      const map = new mapboxgl.Map({
        container: mapContainer.current,
        style: MAPBOX_STYLE,
        center: [centerLongitude, centerLatitude],
        zoom: 13,
        pitch: 60,
        bearing: 0
      });

      // Navigation controls removed per user request

      mapRef.current = map;

      // Resize map after it loads to ensure proper sizing
      const resizeMap = () => {
        if (mapRef.current && mapRef.current.loaded()) {
          mapRef.current.resize();
        }
      };

      if (map.loaded()) {
        resizeMap();
      } else {
        map.once('load', resizeMap);
      }
    }

    // Cleanup function
    return () => {
      // Remove markers
      markersRef.current.forEach(marker => marker.remove());
      markersRef.current = [];
      agentMarkersRef.current.forEach(marker => marker.remove());
      agentMarkersRef.current = [];
      
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, [centerLatitude, centerLongitude]); // Guard `!mapRef.current` ensures initialization only happens once

  // Resize map when container size changes
  useEffect(() => {
    if (!mapRef.current) return;

    let rafId = null;
    const resizeObserver = new ResizeObserver(() => {
      // Use requestAnimationFrame to debounce and avoid ResizeObserver loop warnings
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
      rafId = requestAnimationFrame(() => {
        if (mapRef.current && mapRef.current.loaded()) {
          mapRef.current.resize();
        }
        rafId = null; // Reset after execution
      });
    });

    if (mapContainer.current) {
      resizeObserver.observe(mapContainer.current);
    }

    return () => {
      resizeObserver.disconnect();
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
    };
  }, []);

  // Update map center when center coordinates change
  useEffect(() => {
    if (!mapRef.current) return;
    
    const updateCenter = () => {
      if (mapRef.current.loaded()) {
        mapRef.current.flyTo({
          center: [centerLongitude, centerLatitude],
          zoom: 13,
          duration: 1000
        });
      }
    };

    if (mapRef.current.loaded()) {
      updateCenter();
    } else {
      mapRef.current.once('load', updateCenter);
    }
  }, [centerLatitude, centerLongitude]);

  // Update path when locations or currentTime changes
  useEffect(() => {
    if (!mapRef.current || !locations || locations.length === 0) return;

    const map = mapRef.current;

    const updatePath = () => {
      // Get past locations based on currentTime
      let pastLocations = [];

      if (currentTime !== null) {
        pastLocations = locations.filter(loc => loc.timestamp <= currentTime);
      } else {
        // If no currentTime, show all as past
        pastLocations = locations;
      }

      // Remove existing layer and source
      if (map.getLayer('path-past')) {
        map.removeLayer('path-past');
      }
      if (map.getSource('path-past')) {
        map.removeSource('path-past');
      }

      // Add past path (dotted purple line)
      if (pastLocations.length > 1) {
        const pastCoordinates = pastLocations.map(loc => [loc.lon, loc.lat]);
        
        map.addSource('path-past', {
          type: 'geojson',
          data: {
            type: 'Feature',
            properties: {},
            geometry: {
              type: 'LineString',
              coordinates: pastCoordinates
            }
          }
        });

        map.addLayer({
          id: 'path-past',
          type: 'line',
          source: 'path-past',
          layout: {
            'line-join': 'round',
            'line-cap': 'round'
          },
          paint: {
            'line-color': '#9D4EDD', // Purple color
            'line-width': 3,
            'line-opacity': 0.9,
            'line-dasharray': [2, 2] // Creates dotted effect
          }
        });
      }
    };

    if (map.loaded()) {
      updatePath();
    } else {
      // Set up one-time load listener
      const loadHandler = () => {
        updatePath();
        map.off('load', loadHandler);
      };
      map.on('load', loadHandler);
      
      // Cleanup: remove listener if component unmounts or dependencies change
      return () => {
        if (map.loaded()) {
          map.off('load', loadHandler);
        }
      };
    }
  }, [locations, currentTime]);

  // Highlight a selected feature segment (line + points). Only one segment can be highlighted at a time.
  useEffect(() => {
    if (!mapRef.current) return;
    const map = mapRef.current;

    const SOURCE_ID = 'segment-highlight';
    const LAYER_LINE_ID = 'segment-highlight-line';
    const LAYER_POINTS_ID = 'segment-highlight-points';

    const removeIfExists = () => {
      if (map.getLayer(LAYER_POINTS_ID)) map.removeLayer(LAYER_POINTS_ID);
      if (map.getLayer(LAYER_LINE_ID)) map.removeLayer(LAYER_LINE_ID);
      if (map.getSource(SOURCE_ID)) map.removeSource(SOURCE_ID);
    };

    const update = () => {
      removeIfExists();

      if (!highlightedSegment || !locations || locations.length === 0) {
        return;
      }

      const start = Number(highlightedSegment.startTime);
      const end = Number(highlightedSegment.endTime);
      if (!Number.isFinite(start) || !Number.isFinite(end)) return;

      const segLocations = locations.filter((loc) => loc && loc.timestamp >= start && loc.timestamp <= end);
      if (segLocations.length === 0) return;

      const coordinates = segLocations.map((loc) => [loc.lon, loc.lat]);
      const featureType = highlightedSegment.featureType === 'lift' ? 'lift' : 'run';
      const color = featureType === 'lift' ? '#22c55e' : '#3b82f6';

      map.addSource(SOURCE_ID, {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features: [
            {
              type: 'Feature',
              properties: { feature_type: featureType },
              geometry: { type: 'LineString', coordinates }
            },
            ...coordinates.map((c) => ({
              type: 'Feature',
              properties: { feature_type: featureType },
              geometry: { type: 'Point', coordinates: c }
            }))
          ]
        }
      });

      map.addLayer({
        id: LAYER_LINE_ID,
        type: 'line',
        source: SOURCE_ID,
        filter: ['==', ['geometry-type'], 'LineString'],
        layout: { 'line-join': 'round', 'line-cap': 'round' },
        paint: {
          'line-color': color,
          'line-width': 6,
          'line-opacity': 0.95
        }
      });

      map.addLayer({
        id: LAYER_POINTS_ID,
        type: 'circle',
        source: SOURCE_ID,
        filter: ['==', ['geometry-type'], 'Point'],
        paint: {
          'circle-radius': 4,
          'circle-color': color,
          'circle-opacity': 0.95,
          'circle-stroke-width': 1,
          'circle-stroke-color': '#000000'
        }
      });
    };

    if (map.loaded()) {
      update();
    } else {
      const loadHandler = () => {
        update();
        map.off('load', loadHandler);
      };
      map.on('load', loadHandler);
      return () => {
        if (map.loaded()) map.off('load', loadHandler);
      };
    }

    return () => {
      if (!mapRef.current) return;
      const m = mapRef.current;
      if (!m.loaded()) return;
      // Clean up highlight layers when switching segments/unmounting
      if (m.getLayer(LAYER_POINTS_ID)) m.removeLayer(LAYER_POINTS_ID);
      if (m.getLayer(LAYER_LINE_ID)) m.removeLayer(LAYER_LINE_ID);
      if (m.getSource(SOURCE_ID)) m.removeSource(SOURCE_ID);
    };
  }, [highlightedSegment, locations]);

  // Fetch profile picture URLs from storage when agentPositions change
  useEffect(() => {
    const fetchProfilePictures = async () => {
      const pathsNeedingPictures = agentPositions
        .filter(pos => pos.profilePicturePath && !profilePictureUrls.has(pos.profilePicturePath))
        .map(pos => pos.profilePicturePath);

      if (pathsNeedingPictures.length === 0) {
        return;
      }

      const urlPromises = pathsNeedingPictures.map(async (path) => {
        const url = await getProfilePictureUrl(path);
        return { path, url };
      });

      const results = await Promise.all(urlPromises);
      setProfilePictureUrls(prevUrls => {
        const newUrls = new window.Map(prevUrls);
        results.forEach(({ path, url }) => {
          if (url) {
            newUrls.set(path, url);
          }
        });
        return newUrls;
      });
    };

    if (agentPositions.length > 0) {
      fetchProfilePictures();
    }
    // Note: profilePictureUrls is intentionally excluded from dependencies to prevent infinite loops.
    // We use functional setState to access the latest state without needing it as a dependency.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentPositions]);

  // Update agent markers
  useEffect(() => {
    if (!mapRef.current) return;

    const map = mapRef.current;

    const updateAgentMarkers = () => {
      if (!agentPositions || agentPositions.length === 0) {
        // Remove all agent markers if no positions
        agentMarkersRef.current.forEach(marker => marker.remove());
        agentMarkersRef.current = [];
        return;
      }

      // Create a map of existing markers by agent index
      const existingMarkers = new window.Map();
      agentMarkersRef.current.forEach((marker) => {
        const markerData = marker._agentData;
        if (markerData && markerData.index !== void 0) {
          existingMarkers.set(markerData.index, marker);
        }
      });

      // Process each agent position
      const newMarkers = [];
      agentPositions.forEach((agentPos) => {
        const agentIndex = agentPos.index;
        
        // Check if position is valid (visible flag and valid coordinates)
        const hasValidPosition = agentPos.visible && 
          agentPos.lat !== null && 
          agentPos.lat !== void 0 && 
          agentPos.lon !== null && 
          agentPos.lon !== void 0;
        
        if (hasValidPosition) {
          
          let marker = existingMarkers.get(agentIndex);
          
          if (marker) {
            // Update existing marker position (only if it changed)
            const currentLngLat = marker.getLngLat();
            if (Math.abs(currentLngLat.lng - agentPos.lon) > 0.000001 || 
                Math.abs(currentLngLat.lat - agentPos.lat) > 0.000001) {
              marker.setLngLat([agentPos.lon, agentPos.lat]);
            }
            // Update marker content (profile picture or initials)
            const el = marker.getElement();
            const profilePictureUrl = agentPos.profilePicturePath ? profilePictureUrls.get(agentPos.profilePicturePath) : null;
            
            // Update border for live agents (red border), virtual agents (green border)
            const borderColor = agentPos.isLive ? '#ef4444' : '#22c55e';
            const borderWidth = agentPos.isLive ? '3px' : '2px';
            el.style.border = `${borderWidth} solid ${borderColor}`;
            
            // Update profile picture or initials
            const existingImg = el.querySelector('img');
            const existingInitials = el.querySelector('.agent-initials');
            
            if (profilePictureUrl) {
              // Show profile picture, hide initials
              if (existingImg) {
                if (existingImg.src !== profilePictureUrl) {
                  existingImg.src = profilePictureUrl;
                }
              } else {
                // Create image element
                const img = document.createElement('img');
                img.src = profilePictureUrl;
                img.alt = agentPos.name || 'Agent';
                img.style.width = '100%';
                img.style.height = '100%';
                img.style.borderRadius = '50%';
                img.style.objectFit = 'cover';
                el.appendChild(img);
              }
              if (existingInitials) {
                existingInitials.style.display = 'none';
              }
              // Remove background color if we have an image
              el.style.backgroundColor = '';
            } else {
              // Show initials, hide image
              if (existingImg) {
                existingImg.remove();
              }
              // Set background color for initials fallback
              el.style.backgroundColor = agentPos.isLive ? '#ef4444' : '#22c55e';
              
              if (existingInitials) {
                existingInitials.textContent = agentPos.initials || '?';
                existingInitials.style.display = 'flex';
              } else {
                const initialsSpan = document.createElement('span');
                initialsSpan.className = 'agent-initials';
                initialsSpan.textContent = agentPos.initials || '?';
                initialsSpan.style.display = 'flex';
                initialsSpan.style.alignItems = 'center';
                initialsSpan.style.justifyContent = 'center';
                initialsSpan.style.width = '100%';
                initialsSpan.style.height = '100%';
                el.appendChild(initialsSpan);
              }
            }
            
            newMarkers.push(marker);
            existingMarkers.delete(agentIndex); // Mark as still needed
          } else {
            // Create new marker
            const el = document.createElement('div');
            el.className = 'agent-marker';
            el.style.width = '32px';
            el.style.height = '32px';
            el.style.borderRadius = '50%';
            el.style.cursor = 'pointer';
            el.style.display = 'flex';
            el.style.alignItems = 'center';
            el.style.justifyContent = 'center';
            el.style.fontSize = '12px';
            el.style.fontWeight = 'bold';
            el.style.color = '#ffffff';
            el.style.pointerEvents = 'auto'; // Ensure clicks work
            el.style.overflow = 'hidden'; // For profile picture
            // DO NOT set position - Mapbox handles positioning via transforms
            el.title = agentPos.name || 'Agent';
            
            // Border: red for live agents (3px), green for virtual agents (2px)
            const borderColor = agentPos.isLive ? '#ef4444' : '#22c55e';
            const borderWidth = agentPos.isLive ? '3px' : '2px';
            el.style.border = `${borderWidth} solid ${borderColor}`;
            el.style.boxSizing = 'border-box';
            
            // Add profile picture or initials
            const profilePictureUrl = agentPos.profilePicturePath ? profilePictureUrls.get(agentPos.profilePicturePath) : null;
            if (profilePictureUrl) {
              const img = document.createElement('img');
              img.src = profilePictureUrl;
              img.alt = agentPos.name || 'Agent';
              img.style.width = '100%';
              img.style.height = '100%';
              img.style.borderRadius = '50%';
              img.style.objectFit = 'cover';
              el.appendChild(img);
            } else {
              // Fallback to initials with background color
              el.style.backgroundColor = agentPos.isLive ? '#ef4444' : '#22c55e';
              const initialsSpan = document.createElement('span');
              initialsSpan.className = 'agent-initials';
              initialsSpan.textContent = agentPos.initials || '?';
              initialsSpan.style.display = 'flex';
              initialsSpan.style.alignItems = 'center';
              initialsSpan.style.justifyContent = 'center';
              initialsSpan.style.width = '100%';
              initialsSpan.style.height = '100%';
              el.appendChild(initialsSpan);
            }
            
            // Add click handler - use both click and mousedown for better compatibility
            if (onAgentClick && agentPos.index !== void 0) {
              const handleClick = (e) => {
                e.stopPropagation();
                e.preventDefault();
                onAgentClick(agentPos.index);
              };
              el.addEventListener('click', handleClick);
              el.addEventListener('mousedown', (e) => {
                e.stopPropagation();
              });
            }

            marker = new mapboxgl.Marker({
              element: el,
              anchor: 'center',
              offset: [0, 0] // Explicitly set offset to ensure proper positioning
            })
              .setLngLat([agentPos.lon, agentPos.lat])
              .addTo(map);
            
            // Store agent data on marker for tracking
            marker._agentData = { index: agentIndex };
            newMarkers.push(marker);
          }
        }
      });
      
      // Update the markers array
      agentMarkersRef.current = newMarkers;

      // Remove markers that are no longer needed
      existingMarkers.forEach((marker) => {
        marker.remove();
        const idx = agentMarkersRef.current.indexOf(marker);
        if (idx > -1) {
          agentMarkersRef.current.splice(idx, 1);
        }
      });
    };

    if (map.loaded()) {
      updateAgentMarkers();
    } else {
      const loadHandler = () => {
        updateAgentMarkers();
        map.off('load', loadHandler);
      };
      map.on('load', loadHandler);
      
      return () => {
        if (map.loaded()) {
          map.off('load', loadHandler);
        }
      };
    }
  }, [agentPositions, onAgentClick, profilePictureUrls]);

  // Move agent markers on every published frame (LVC). `agentPositions` still owns marker
  // identity and appearance, which change rarely; this only moves them, so motion is not
  // tied to React's render rate.
  useEffect(() => {
    if (!positionSource) return void 0;

    return positionSource.subscribe((states) => {
      if (!agentMarkersRef.current.length) return;
      const byIndex = new window.Map();
      agentMarkersRef.current.forEach((marker) => {
        const index = marker._agentData?.index;
        if (index !== void 0) byIndex.set(index, marker);
      });

      states.forEach((state, index) => {
        const marker = byIndex.get(index);
        const position = state?.currentPosition;
        if (!marker || !position || position.lat === null || position.lat === void 0
          || position.lon === null || position.lon === void 0) return;
        const current = marker.getLngLat();
        if (Math.abs(current.lng - position.lon) > 0.0000001 || Math.abs(current.lat - position.lat) > 0.0000001) {
          marker.setLngLat([position.lon, position.lat]);
        }
      });
    });
  }, [positionSource]);

  // Update current position marker when currentTime changes (for dataset paths)
  useEffect(() => {
    if (!mapRef.current) return;

    const map = mapRef.current;

    // Remove marker if agent positions are present (agents have their own markers)
    if (agentPositions.length > 0) {
      markersRef.current.forEach(marker => marker.remove());
      markersRef.current = [];
      return;
    }

    // Only show marker for dataset paths when we have locations and currentTime
    if (!locations || locations.length === 0 || currentTime === null) {
      markersRef.current.forEach(marker => marker.remove());
      markersRef.current = [];
      return;
    }

    // Find the location closest to currentTime
    const currentLocation = locations.reduce((prev, curr) => {
      return Math.abs(curr.timestamp - currentTime) < Math.abs(prev.timestamp - currentTime) 
        ? curr 
        : prev;
    });

    // Update existing marker or create new one
    if (markersRef.current.length > 0) {
      // Update existing marker position
      const marker = markersRef.current[0];
      const currentLngLat = marker.getLngLat();
      // Only update if position changed significantly (avoid unnecessary updates)
      if (Math.abs(currentLngLat.lng - currentLocation.lon) > 0.000001 || 
          Math.abs(currentLngLat.lat - currentLocation.lat) > 0.000001) {
        marker.setLngLat([currentLocation.lon, currentLocation.lat]);
      }
    } else {
      // Create new marker if none exists
      const marker = new mapboxgl.Marker({ 
        color: '#ffffff',
        scale: 1.2
      })
        .setLngLat([currentLocation.lon, currentLocation.lat])
        .addTo(map);

      markersRef.current.push(marker);
    }

    // Center map on current position (only update if map is loaded)
    if (map.loaded()) {
      map.flyTo({
        center: [currentLocation.lon, currentLocation.lat],
        zoom: 15,
        pitch: 60,
        bearing: currentLocation.course || 0,
        duration: 1000
      });
    }
  }, [currentTime, locations, agentPositions.length]);

  return (
    <div 
      ref={mapContainer} 
      className="mapbox-container" 
      style={{ height: height, width: '100%', position: 'relative' }}
    >
      {/* Map will be rendered here */}
    </div>
  );
};

export default Map;

